"use client";
import { useId, useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";

import classNames from "classnames";

import EmlakjetLogo from "assets/svg/logo.svg";
import InfoSvg from "assets/svg/info_small.svg";

import { tooltipAlternatifBank } from "constants/alternatif-bank";

import { throttle } from "@/src/utils/throttle";
import { Ga4WithEventParameters } from "src/helpers/google-tag-manager/events/ga4-with-eventparameters";

import Container from "components/grid/container";
import Price from "components/common/price";
import PriceTrend from "components/price-trend";
import SendMessageButton from "components/common/send-message-button";
import { LazyImage } from "components/common/lazy-image/LazyImage";
import ShowPhone from "../../common/show-phone/ShowPhone";
import { useEnvContext } from "next-runtime-env";

import styles from "./styles.module.css";

const StickyHeader = ({ data }) => {
  const keyId = useId();
  const wrapperRef = useRef();
  const { NEXT_PUBLIC_EJ_APP_CANONICAL_HOST } = useEnvContext();
  const reduxStickyMenuItems = useSelector((state) => state.listingDetail.stickyHeaderMenuItems);

  const [isMenuVisible, setIsMenuVisible] = useState(false);
  const [selectedKey, setSelectedKey] = useState("");

  const {
    id,
    title,
    priceDetail: { price, currency, trendType, differenceRate, alternativeValue },
    quickInfos,
    owner: {
      account: { phoneNumber },
    },
    listingLogo,
  } = data;

  const listingEstateType = data?.properties?.filter((item) => item.key === "category")[0].value;
  const isSale = data.tradeType?.key === "sale";
  const isRent = data.tradeType?.key === "rent";
  const hasBanner = ["Konut"].includes(listingEstateType) && (isSale || isRent);

  const wrapperClasses = classNames({
    [styles.wrapper]: true,
    [styles.wrapperIsVisible]: isMenuVisible,
  });

  const handleMenuItem = (key) => {
    Ga4WithEventParameters({
      eventName: "click_widget",
      eventParameters: { widget: `sticky-menu||${key}` },
    });
    setSelectedKey(key);
    const wrapperHeight = wrapperRef.current.clientHeight || 0;
    window.scrollTo({
      top: document.getElementById(key).offsetTop - wrapperHeight / 2,
      left: 0,
      behavior: "smooth",
    });
  };

  const toggleMenuVisibility = () => {
    setIsMenuVisible(window.scrollY > 100);
    const genelBakisOffsetVisibilityHeight = 532;
    if (window.scrollY < genelBakisOffsetVisibilityHeight) setSelectedKey("genel-bakis");
  };

  const activeMenuForScroll = () => {
    const elements = document.querySelectorAll("[data-scroll-selector]");
    let elementId = null;
    elements.forEach((item) => {
      elementId = item.getAttribute("id");
      if (window.pageYOffset >= item.offsetTop - 110 && selectedKey !== elementId) {
        setSelectedKey(elementId);
      }
    });
  };

  const handleClickAkbankIcon = () => {
    const element = document.getElementById("akbank-banner");
    if (element) {
      const headerOffset = wrapperRef.current?.clientHeight || 120;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }

    setSelectedKey("akbank-banner");

    setTimeout(() => {
      Ga4WithEventParameters({
        eventName: "click_widget",
        eventParameters: { widget: "sticky-menu || akbank_firsati" },
      });
    }, 500);
  };

  useEffect(() => {
    toggleMenuVisibility();

    window.addEventListener("scroll", throttle(toggleMenuVisibility, 250), false);
    window.addEventListener("scroll", throttle(activeMenuForScroll, 250), false);

    return () => {
      window.removeEventListener("scroll", throttle(toggleMenuVisibility, 250));
      window.removeEventListener("scroll", throttle(activeMenuForScroll, 250));
    };
  }, [typeof window !== "undefined"]);

  return (
    <div className={wrapperClasses} ref={wrapperRef}>
      <div className={styles.topWrap}>
        <a href={`${NEXT_PUBLIC_EJ_APP_CANONICAL_HOST}`} className={styles.logoWrap}>
          <EmlakjetLogo />
        </a>
        <div className={styles.containerWrap}>
          <Container>
            <div className={styles.inner}>
              <div className={styles.contentWrap}>
                <span className={styles.title}>{title}</span>
                <div className={styles.detail}>
                  <span className={styles.priceWrap}>
                    {price ? <Price price={price} currency={currency} /> : alternativeValue}
                  </span>
                  {trendType === "down" && <PriceTrend data={differenceRate} />}
                  {quickInfos ? (
                    <ul className={styles.infoList}>
                      {quickInfos.map((item, index) => (
                        <li key={`${keyId}-${index}`}>{item.value}</li>
                      ))}
                    </ul>
                  ) : null}
                </div>
                {listingLogo && (
                  <div className={styles.alternatifBankWrap}>
                    <LazyImage
                      src="/assets/images/alternatif_bank.png"
                      alt="/assets/images/alternatif_bank.png"
                      className={styles.alternatifBankLogo}
                    />
                    <span className={styles.alternatifBankSeperator}></span>
                    <div className={styles.alternatifBankText}>
                      Alternatif Bank İş Birliği
                      {/* eslint-disable-next-line */}
                      <span className={styles.iconWrap} tooltip={tooltipAlternatifBank}>
                        <InfoSvg />
                      </span>
                    </div>
                  </div>
                )}
              </div>
              <div className={styles.actionWrap}>
                <div className={styles.buttonClass}>
                  <SendMessageButton listingId={id} />
                </div>
                <div className={styles.buttonClass}>
                  <ShowPhone listItemInfo={data} phoneNumber={phoneNumber} gtmEventLabel="detay" />
                </div>
              </div>
            </div>
          </Container>
        </div>
      </div>
      {reduxStickyMenuItems ? (
        <div className={styles.bottomWrap}>
          <Container>
            <ul className={styles.menu}>
              {reduxStickyMenuItems?.map((item, index) => (
                <>
                  <li
                    key={`${keyId}+${index}`}
                    onClick={() => handleMenuItem(item.key)}
                    className={classNames({ [styles.active]: selectedKey === item.key })}
                  >
                    {item.value}
                  </li>

                  {hasBanner && item.key === "bolge-raporu" && (
                    <li
                      role="presentation"
                      className={classNames(styles.akbankManualItem, {
                        [styles.active]: selectedKey === "akbank-banner",
                      })}
                      onClick={handleClickAkbankIcon}
                    >
                      <img src="/assets/images/akbank-icon.webp" alt="akbank-icon" />
                      Fırsatı
                    </li>
                  )}
                </>
              ))}
            </ul>
          </Container>
        </div>
      ) : null}
    </div>
  );
};

StickyHeader.propTypes = {
  data: PropTypes.object,
};

export default StickyHeader;
